import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Chip,
  Button,
  Snackbar,
  Alert,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import { SAGE, CREAM } from '../theme';
import { getPreferences, savePreferences } from '../services/preferencesService';
import { mockAuthHeaders } from '../utils/mockAuth';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const DEFAULT_MARKET_LABELS = {
  seattle_msa: 'Seattle', us_remote: 'US Remote', global_remote: 'Global Remote',
  sf_bay_area: 'SF Bay Area', nyc_metro: 'NYC', la_metro: 'Los Angeles',
  boston_msa: 'Boston', chicago_msa: 'Chicago', dc_metro: 'Washington DC',
  austin_msa: 'Austin', denver_msa: 'Denver', atlanta_msa: 'Atlanta',
  portland_msa: 'Portland', non_us: 'International',
};

const PreferencesPage = () => {
  const [taxonomy, setTaxonomy] = useState(null);
  const [marketsTaxonomy, setMarketsTaxonomy] = useState(null);
  const [prefs, setPrefs] = useState({ roles: [], disciplines: [], markets: [], levels: [], companies: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState(null);

  // For the discipline → role drill-down
  const [expandedDisc, setExpandedDisc] = useState(null);

  useEffect(() => {
    const headers = { ...mockAuthHeaders() };
    Promise.all([
      fetch(`${API_URL}/api/taxonomy`, { headers, credentials: 'include' }).then((r) => r.json()),
      fetch(`${API_URL}/api/taxonomy/markets`, { headers, credentials: 'include' }).then((r) => r.json()),
      getPreferences(),
    ])
      .then(([taxData, mktsData, prefsData]) => {
        setTaxonomy(taxData);
        setMarketsTaxonomy(mktsData);
        setPrefs(prefsData);
      })
      .catch((err) => console.error('Failed to load:', err))
      .finally(() => setLoading(false));
  }, []);

  const toggle = (field, value) => {
    setPrefs((prev) => {
      const arr = prev[field] || [];
      const next = arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value];
      return { ...prev, [field]: next };
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await savePreferences(prefs);
      setSnackbar({ severity: 'success', message: 'Preferences saved' });
    } catch (err) {
      setSnackbar({ severity: 'error', message: 'Failed to save preferences' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress sx={{ color: SAGE[500] }} />
      </Box>
    );
  }

  const departments = taxonomy?.departments || [];
  const disciplines = taxonomy?.disciplines || [];
  const roles = taxonomy?.roles || [];
  const markets = marketsTaxonomy?.markets || [];

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontFamily: "'Outfit', sans-serif", fontWeight: 700, color: SAGE[900] }}>
            Preferences
          </Typography>
          <Typography variant="body2" sx={{ color: SAGE[500], mt: 0.5 }}>
            Select the roles and markets you're interested in. These will auto-apply as filters across Jobs and Insights.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={saving}
          sx={{
            bgcolor: SAGE[700], textTransform: 'none', fontFamily: "'Outfit', sans-serif",
            borderRadius: 2, '&:hover': { bgcolor: SAGE[800] },
          }}
        >
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </Box>

      {/* Markets */}
      <Section title="Markets" subtitle="Where are you looking for jobs?">
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Remote markets */}
          <Box>
            <Typography sx={{ fontSize: '0.75rem', color: SAGE[400], fontFamily: "'Outfit', sans-serif", mb: 1, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Remote
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
              {markets.filter((m) => m.type === 'remote').map((m) => (
                <SelectableChip
                  key={m.id}
                  label={m.name}
                  selected={prefs.markets.includes(m.id)}
                  onClick={() => toggle('markets', m.id)}
                />
              ))}
            </Box>
          </Box>

          {/* Metro markets */}
          <Box>
            <Typography sx={{ fontSize: '0.75rem', color: SAGE[400], fontFamily: "'Outfit', sans-serif", mb: 1, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Metro Areas
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
              {markets.filter((m) => m.type === 'metro').map((m) => (
                <SelectableChip
                  key={m.id}
                  label={m.name}
                  selected={prefs.markets.includes(m.id)}
                  onClick={() => toggle('markets', m.id)}
                />
              ))}
            </Box>
          </Box>

          {/* International */}
          <Box>
            <Typography sx={{ fontSize: '0.75rem', color: SAGE[400], fontFamily: "'Outfit', sans-serif", mb: 1, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              International
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
              {markets.filter((m) => m.type === 'international').map((m) => (
                <SelectableChip
                  key={m.id}
                  label={m.name}
                  selected={prefs.markets.includes(m.id)}
                  onClick={() => toggle('markets', m.id)}
                />
              ))}
            </Box>
          </Box>
        </Box>
      </Section>

      {/* Roles — grouped by department → discipline → roles */}
      <Section title="Roles" subtitle="What roles are you interested in? Select disciplines to see specific roles.">
        {departments.map((dept) => {
          const deptDiscs = disciplines.filter((d) => d.department_ids.includes(dept.id));
          return (
            <Box key={dept.id} sx={{ mb: 2.5 }}>
              <Typography sx={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600, fontSize: '0.9rem', color: SAGE[800], mb: 1 }}>
                {dept.name}
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 1 }}>
                {deptDiscs.map((disc) => {
                  const isExpanded = expandedDisc === disc.id;
                  const discRoles = roles.filter((r) => r.discipline_id === disc.id);
                  const selectedCount = discRoles.filter((r) => prefs.roles.includes(r.id)).length;

                  return (
                    <SelectableChip
                      key={disc.id}
                      label={`${disc.name}${selectedCount > 0 ? ` (${selectedCount})` : ''}`}
                      selected={isExpanded}
                      onClick={() => setExpandedDisc(isExpanded ? null : disc.id)}
                      variant="outlined"
                    />
                  );
                })}
              </Box>

              {/* Expanded roles for selected discipline */}
              {deptDiscs.map((disc) => {
                if (expandedDisc !== disc.id) return null;
                const discRoles = roles.filter((r) => r.discipline_id === disc.id);
                return (
                  <Box key={`roles-${disc.id}`} sx={{ pl: 2, pt: 1, pb: 1, borderLeft: `2px solid ${SAGE[200]}`, ml: 1 }}>
                    <Typography sx={{ fontSize: '0.72rem', color: SAGE[400], fontFamily: "'Outfit', sans-serif", mb: 1 }}>
                      Select roles in {disc.name}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                      {discRoles.map((role) => (
                        <SelectableChip
                          key={role.id}
                          label={role.name}
                          selected={prefs.roles.includes(role.id)}
                          onClick={() => toggle('roles', role.id)}
                        />
                      ))}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          );
        })}
      </Section>

      {/* Levels */}
      <Section title="Seniority Levels" subtitle="What level are you targeting?">
        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
          {['Entry', 'Junior', 'Senior', 'Staff', 'Senior Staff', 'Principal', 'Lead', 'Manager', 'Senior Manager', 'Director', 'VP'].map((lv) => (
            <SelectableChip
              key={lv}
              label={lv}
              selected={prefs.levels.includes(lv)}
              onClick={() => toggle('levels', lv)}
            />
          ))}
        </Box>
      </Section>

      <Snackbar
        open={!!snackbar}
        autoHideDuration={3000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snackbar && (
          <Alert severity={snackbar.severity} onClose={() => setSnackbar(null)} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        )}
      </Snackbar>
    </Box>
  );
};


const Section = ({ title, subtitle, children }) => (
  <Box sx={{ mb: 4, p: 3, bgcolor: CREAM[50], borderRadius: 2.5, border: `1px solid ${SAGE[200]}` }}>
    <Typography sx={{ fontFamily: "'Outfit', sans-serif", fontWeight: 700, fontSize: '1.05rem', color: SAGE[900], mb: 0.25 }}>
      {title}
    </Typography>
    {subtitle && (
      <Typography sx={{ fontSize: '0.8rem', color: SAGE[400], fontFamily: "'Outfit', sans-serif", mb: 2 }}>
        {subtitle}
      </Typography>
    )}
    {children}
  </Box>
);


const SelectableChip = ({ label, selected, onClick, variant }) => (
  <Chip
    label={label}
    size="small"
    onClick={onClick}
    variant={variant === 'outlined' && !selected ? 'outlined' : 'filled'}
    sx={{
      fontFamily: "'Outfit', sans-serif",
      fontSize: '0.78rem',
      height: 28,
      cursor: 'pointer',
      bgcolor: selected ? SAGE[700] : CREAM[200],
      color: selected ? '#fff' : SAGE[600],
      borderColor: selected ? SAGE[700] : SAGE[300],
      '&:hover': {
        bgcolor: selected ? SAGE[800] : SAGE[100],
      },
    }}
  />
);


export default PreferencesPage;
